"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { MapPin, Phone, Mail } from "lucide-react";
import { useLanguage } from "../hooks/useLanguage";
import { useWebsiteSettings } from "../contexts/WebsiteSettingsContext";
import PublicNavigation from "./PublicNavigation";
import config from "../config/config";

const Footer = () => {
  const pathname = usePathname();
  const { language } = useLanguage();
  const { settings } = useWebsiteSettings();
  const [socialMediaData, setSocialMediaData] = useState(null);

  // Don't show footer on admin/dashboard routes
  if (pathname?.startsWith("/dashboard")) {
    return null;
  }

  // Fetch social media settings
  useEffect(() => {
    const fetchSocialMedia = async () => {
      try {
        const response = await fetch(`${config.NEXT_PUBLIC_API_URL}/api/public/settings/social-media`);
        const data = await response.json();
        if (data.success && data.data) {
          setSocialMediaData(data.data);
        }
      } catch (error) {
        console.error('Error fetching social media:', error);
      }
    };
    fetchSocialMedia();
  }, []);

  // Get footer settings from context
  const footerSettings = settings?.footer;
  
  // Don't show footer if no settings available
  if (!footerSettings) {
    return null;
  }

  // Helper function to get language-specific text
  const getLocalizedText = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] || field.en || '';
    }
    return '';
  };

  // Get social media profiles for footer
  const footerSocialEnabled = footerSettings.socialMedia?.enabled !== false;
  const socialMediaProfiles = socialMediaData?.profiles || [];
  const footerLimit = socialMediaData?.displaySettings?.footerLimit || 10;
  
  const socialMedia = footerSocialEnabled && Array.isArray(socialMediaProfiles)
    ? socialMediaProfiles
        .filter(profile => profile.isActive && profile.showInFooter)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .slice(0, footerLimit)
        .map(profile => ({
          id: profile.id,
          name: profile.platform.charAt(0).toUpperCase() + profile.platform.slice(1),
          icon: profile.platform,
          url: profile.url
        }))
    : [];
  
  const officeAddress = getLocalizedText(footerSettings.officeAddress);
  const copyright = getLocalizedText(footerSettings.copyright);
  const phone = footerSettings.phone;
  const email = footerSettings.email;
  const description = getLocalizedText(footerSettings.description);

  // Check if there's any content to show
  const hasContent = officeAddress || phone || email || description || 
                     (footerSettings.footerMenu && footerSettings.footerMenu.length > 0) || 
                     socialMedia.length > 0 || copyright;

  if (!hasContent) {
    return null;
  }

  return (
    <footer style={{ backgroundColor: 'var(--color-card)', color: 'var(--color-foreground)', borderTop: '2px solid var(--color-primary)' }}>
      {/* Description Section */}
      {description && (
        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">
          <p className="text-center text-xs" style={{ color: 'var(--color-paragraph)' }}>
            {description}
          </p>
        </div>
      )}
      
      <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8 lg:py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8 lg:gap-12">
          {/* Column 1 - About/Office */}
          {(officeAddress || phone || email) && (
          <div className="space-y-2 sm:space-y-3 md:space-y-4">
            <div className="space-y-1.5 sm:space-y-2 md:space-y-3 text-xs" style={{ color: 'var(--color-paragraph)' }}>
              {officeAddress && (
                <p className="flex items-start gap-1.5 sm:gap-2">
                  <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 sm:mt-1 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
                  <span className="whitespace-pre-line">{officeAddress}</span>
                </p>
              )}
              {phone && (
                <p className="flex items-start gap-1.5 sm:gap-2">
                  <Phone className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 sm:mt-1 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
                  <span>{phone}</span>
                </p>
              )}
              {email && (
                <p className="flex items-start gap-1.5 sm:gap-2">
                  <Mail className="w-3.5 h-3.5 sm:w-4 sm:h-4 mt-0.5 sm:mt-1 flex-shrink-0" style={{ color: 'var(--color-primary)' }} />
                  <span>{email}</span>
                </p>
              )}
            </div>
          </div>
          )}

          {/* Column 2 - Quick Links */}
          {footerSettings.footerMenu && footerSettings.footerMenu.length > 0 && (
          <div className="space-y-2 sm:space-y-3 md:space-y-4">
            <h3 className="text-sm sm:text-base font-bold mb-2 sm:mb-3 md:mb-4" style={{ color: 'var(--color-heading)' }}>
              Quick Links
            </h3>
            <PublicNavigation 
              className="flex flex-col gap-1.5 sm:gap-2 text-xs" 
              isFooter={true} 
              menuItems={footerSettings.footerMenu}
            />
          </div>
          )}

          {/* Column 3 - Social Media */}
          {socialMedia && socialMedia.length > 0 && (
          <div className="space-y-2 sm:space-y-3 md:space-y-4">
            <h3 className="text-sm sm:text-base font-bold mb-2 sm:mb-3 md:mb-4" style={{ color: 'var(--color-heading)' }}>
              Follow Us
            </h3>
            <div className="flex flex-wrap gap-2 sm:gap-2.5 md:gap-3">
              {socialMedia.map((social) => (
                <Link
                  key={social.id || social.icon}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                  style={{
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-primary-foreground)'
                  }}
                  title={social.name}
                >
                  {renderSocialIcon(social.icon)}
                </Link>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Bottom Copyright Bar */}
      {copyright && (
      <div className="border-t" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-muted)' }}>
        <div className="container mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-3 sm:py-4">
          <p className="text-center text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            {copyright}
          </p>
        </div>
      </div>
      )}
    </footer>
  );
};

// Helper function to render social icons
const renderSocialIcon = (iconName) => {
  const iconMap = {
    facebook: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    instagram: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
      </svg>
    ),
    linkedin: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
    twitter: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M18.901 1.153h3.68l-8.04 9.19L24 22.846h-7.406l-5.8-7.584-6.638 7.584H.474l8.6-9.83L0 1.154h7.594l5.243 6.932ZM17.61 20.644h2.039L6.486 3.24H4.298Z" />
      </svg>
    ),
    youtube: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
  };

  return iconMap[iconName?.toLowerCase()] || iconMap.facebook;
};

export default Footer;
